import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CaptchaTracePoint } from '../../types';
import { CaptchaViewProps, pngSrc, toImagePoint } from './captchaUtils';
import { CaptchaHelp } from './CaptchaHelp';

const TARGET_COUNT = 4;

/**
 * Shapes are at least ~48 image px apart (centre to centre), so a tap this close to an
 * earlier tap is on the same shape: it deselects that shape instead of selecting it twice.
 */
export const SAME_SHAPE_RADIUS = 22;

const distance = (a: CaptchaTracePoint, b: CaptchaTracePoint) => Math.hypot(a.x - b.x, a.y - b.y);

/** Tap the shapes shown in the top strip, in order, then confirm. */
export function ClickInOrderCaptcha({ challenge, startedAt, disabled, onSubmit }: CaptchaViewProps) {
  const { t } = useTranslation();
  const [taps, setTaps] = useState<CaptchaTracePoint[]>([]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    const tap = toImagePoint(event, event.currentTarget, challenge.width, startedAt);

    const existing = taps.findIndex(previous => distance(previous, tap) <= SAME_SHAPE_RADIUS);
    if (existing >= 0) {
      setTaps(taps.filter((_, index) => index !== existing));
      return;
    }
    if (taps.length < TARGET_COUNT) {
      setTaps([...taps, tap]);
    }
  };

  return (
    <div className="captcha-click">
      <CaptchaHelp type="click" />
      <div
        className="captcha-image-frame captcha-clickable"
        style={{ aspectRatio: `${challenge.width} / ${challenge.height}` }}
        onClick={handleClick}
      >
        <img className="captcha-image" src={pngSrc(challenge.image)} alt="" draggable={false} />
        {taps.map((tap, index) => (
          <span
            key={`${tap.t}-${index}`}
            className="captcha-tap-marker"
            style={{ left: `${(tap.x / challenge.width) * 100}%`, top: `${(tap.y / challenge.height) * 100}%` }}
          >
            {index + 1}
          </span>
        ))}
      </div>
      <p className="captcha-progress" aria-live="polite">
        {t('captcha.click.progress', { count: taps.length, total: TARGET_COUNT })}
      </p>
      <div className="captcha-button-row">
        <button
          type="button"
          className="btn-modern btn-secondary-modern"
          disabled={disabled || taps.length === 0}
          onClick={() => setTaps([])}
        >
          {t('captcha.click.reset')}
        </button>
        <button
          type="button"
          className="btn-modern btn-primary-modern"
          disabled={disabled || taps.length !== TARGET_COUNT}
          onClick={() => onSubmit({ taps })}
        >
          {t('captcha.check')}
        </button>
      </div>
    </div>
  );
}
